import {
  addBodyworkerProtocolList,
  deleteBodyworkerProtocolList,
  editBodyworkerProtocolList,
} from "@/app/lib/actions/bodyworker-protocol-lists";
import {
  ItemsListCard,
  ItemsListCardSkeleton,
} from "@/components/backoffice/items-list-card";
import {
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { ItemContent, ItemTitle } from "@/components/ui/item";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Switch } from "@/components/ui/switch";
import {
  invalidateBodyworkerProtocolListsQuery,
  useBodyworkerProtocolListsQuery,
} from "@/lib/queries";
import {
  addBodyworkerProtocolListSchema,
  editBodyworkerProtocolListSchema,
} from "@/lib/schemas/bodyworker-protocol-lists";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import * as z from "zod";

type BodyworkerProtocolList = {
  id: number;
  kvps: string;
  commercialDenomination: string;
  postalCode: string;
  siren: string;
  commercialProtocol: "CAR" | "CAR3";
  isActive: boolean;
};

function BodyworkerProtocolItemsListCard() {
  const queryClient = useQueryClient();
  const bodyworkerProtocolListsQuery = useBodyworkerProtocolListsQuery({
    sort: { commercialDenomination: "asc" },
  });

  const handleCreate = async (
    bodyworkerProtocolList: z.infer<typeof addBodyworkerProtocolListSchema>,
  ) => {
    const response = await addBodyworkerProtocolList(bodyworkerProtocolList);
    if (!response.success) {
      toast.error(response.message);
    } else {
      toast.success("Carrossier protocole commercial ajouté.");
      invalidateBodyworkerProtocolListsQuery(queryClient);
    }
  };

  const handleUpdate = async (
    bodyworkerProtocolList: z.infer<typeof editBodyworkerProtocolListSchema>,
  ) => {
    const response = await editBodyworkerProtocolList(bodyworkerProtocolList);
    if (!response.success) {
      toast.error(response.message);
    } else {
      toast.success("Carrossier protocole commercial modifié.");
      invalidateBodyworkerProtocolListsQuery(queryClient);
    }
  };

  const handleDelete = async (id: { id: number }) => {
    const response = await deleteBodyworkerProtocolList(id);
    if (!response.success) {
      toast.error(response.message);
    } else {
      toast.success("Carrossier protocole commercial supprimé.");
      invalidateBodyworkerProtocolListsQuery(queryClient);
    }
  };

  if (bodyworkerProtocolListsQuery.isPending) return <ItemsListCardSkeleton />;
  const bodyworkerProtocolLists =
    bodyworkerProtocolListsQuery.data?.bodyworkerProtocolLists ?? [];

  return (
    <ItemsListCard<BodyworkerProtocolList>
      title="Carrossiers protocoles commerciaux"
      items={bodyworkerProtocolLists}
      createSchema={addBodyworkerProtocolListSchema}
      editSchema={editBodyworkerProtocolListSchema}
      defaultCreateValues={{
        commercialDenomination: "",
        kvps: "",
        postalCode: "",
        siren: "",
        commercialProtocol: "CAR",
        isActive: true,
      }}
      onCreate={handleCreate}
      onUpdate={handleUpdate}
      onDelete={handleDelete}
      renderItem={(item) => (
        <ItemContent>
          <ItemTitle>
            {item.commercialDenomination} - {item.kvps} - {item.postalCode}
          </ItemTitle>
        </ItemContent>
      )}
      renderFormFields={(form) => (
        <FieldGroup>
          <form.Field name="commercialDenomination">
            {(field) => {
              const isInvalid =
                field.state.meta.isTouched && !field.state.meta.isValid;
              return (
                <Field data-invalid={isInvalid}>
                  <FieldLabel htmlFor={field.name}>
                    Dénomination commerciale
                  </FieldLabel>
                  <Input
                    id={field.name}
                    name={field.name}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                    aria-invalid={isInvalid}
                    maxLength={200}
                  />
                  {isInvalid && <FieldError errors={field.state.meta.errors} />}
                </Field>
              );
            }}
          </form.Field>
          <form.Field name="kvps">
            {(field) => {
              const isInvalid =
                field.state.meta.isTouched && !field.state.meta.isValid;
              return (
                <Field data-invalid={isInvalid}>
                  <FieldLabel htmlFor={field.name}>KVPS</FieldLabel>
                  <Input
                    id={field.name}
                    name={field.name}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                    aria-invalid={isInvalid}
                    maxLength={9}
                  />
                  {isInvalid && <FieldError errors={field.state.meta.errors} />}
                </Field>
              );
            }}
          </form.Field>
          <form.Field name="postalCode">
            {(field) => {
              const isInvalid =
                field.state.meta.isTouched && !field.state.meta.isValid;
              return (
                <Field data-invalid={isInvalid}>
                  <FieldLabel htmlFor={field.name}>Code postal</FieldLabel>
                  <Input
                    id={field.name}
                    name={field.name}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                    aria-invalid={isInvalid}
                    maxLength={5}
                  />
                  {isInvalid && <FieldError errors={field.state.meta.errors} />}
                </Field>
              );
            }}
          </form.Field>
          <form.Field name="siren">
            {(field) => {
              const isInvalid =
                field.state.meta.isTouched && !field.state.meta.isValid;
              return (
                <Field data-invalid={isInvalid}>
                  <FieldLabel htmlFor={field.name}>SIREN</FieldLabel>
                  <Input
                    id={field.name}
                    name={field.name}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                    aria-invalid={isInvalid}
                    maxLength={9}
                  />
                  {isInvalid && <FieldError errors={field.state.meta.errors} />}
                </Field>
              );
            }}
          </form.Field>
          <form.Field name="commercialProtocol">
            {(field) => (
              <Field>
                <FieldLabel>Protocole commercial</FieldLabel>
                <RadioGroup
                  className="flex flex-wrap gap-4"
                  value={field.state.value}
                  onValueChange={(value) =>
                    field.handleChange(value as "CAR" | "CAR3")
                  }
                >
                  <div className="flex items-center gap-2">
                    <RadioGroupItem id={`${field.name}-car`} value="CAR" />
                    <Label htmlFor={`${field.name}-car`}>CAR</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <RadioGroupItem id={`${field.name}-car3`} value="CAR3" />
                    <Label htmlFor={`${field.name}-car3`}>CAR3</Label>
                  </div>
                </RadioGroup>
              </Field>
            )}
          </form.Field>
          <form.Field name="isActive">
            {(field) => (
              <Field>
                <FieldLabel htmlFor={field.name}>Statut</FieldLabel>
                <Switch
                  id="switch"
                  checked={field.state.value}
                  onCheckedChange={field.handleChange}
                />
              </Field>
            )}
          </form.Field>
        </FieldGroup>
      )}
    />
  );
}

export { BodyworkerProtocolItemsListCard };
